import { CountryArray } from './Country.js';
import { LanguageArray } from './Language.js';
/**
 * Map of each supported country to its default language.
 */
const CountryDefaultLanguage = {
    US: 'en',
    PL: 'pl',
};
/**
 * Get the default language for a country.
 * @util
 * @__NO_SIDE_EFFECTS__
 */
export function getDefaultLanguage(country) {
    return CountryDefaultLanguage[country];
}
/**
 * Format a locale string from country and language.
 * When using the default language for a country, returns just the lowercase country code.
 * When using a non-default language, returns `{country}-{lang}` (both lowercase).
 *
 * @example
 * formatLocale('US', 'en') // 'us'
 * formatLocale('PL', 'pl') // 'pl'
 * formatLocale('PL', 'en') // 'pl-en'
 * formatLocale('US', 'pl') // 'us-pl'
 *
 * @util
 * @__NO_SIDE_EFFECTS__
 */
export function formatLocale(country, language) {
    const countryLower = country.toLowerCase();
    if (CountryDefaultLanguage[country] === language) {
        return countryLower;
    }
    return `${countryLower}-${language}`;
}
/**
 * Parse a locale string into its country and language components.
 * Returns null if the locale string is invalid.
 *
 * @example
 * parseLocale('us')    // { country: 'US', language: 'en' }
 * parseLocale('pl')    // { country: 'PL', language: 'pl' }
 * parseLocale('pl-en') // { country: 'PL', language: 'en' }
 * parseLocale('us-pl') // { country: 'US', language: 'pl' }
 * parseLocale('xx')    // null
 *
 * @util
 * @__NO_SIDE_EFFECTS__
 */
export function parseLocale(locale) {
    const parts = locale.split('-');
    if (parts.length === 1) {
        // Just country code with default language
        const country = parts[0].toUpperCase();
        if (!CountryArray.includes(country)) {
            return null;
        }
        return {
            country,
            language: CountryDefaultLanguage[country],
        };
    }
    if (parts.length === 2) {
        const country = parts[0].toUpperCase();
        const language = parts[1].toLowerCase();
        if (!CountryArray.includes(country) || !LanguageArray.includes(language)) {
            return null;
        }
        return { country, language };
    }
    return null;
}
/**
 * Pre-computed list of all valid locale strings.
 */
export const SupportedLocales = computeSupportedLocales();
function computeSupportedLocales() {
    const locales = [];
    for (const country of CountryArray) {
        for (const language of LanguageArray) {
            locales.push(formatLocale(country, language));
        }
    }
    return locales;
}
//# sourceMappingURL=Locale.js.map